import { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { ArrowLeft } from 'lucide-react';
import { ProjectTypePill } from '@/components/ProjectTypePill';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import UserIconDropDown from './UserIconDropDown';
import Routes from '@/router/routes';
import useTranslation from '@/hooks/useTranslation';
import ProjectStatusMenu from './ProjectStatusMenu';
import { ECompanyThemeName, EProjectStatus } from '@/enums';
import useGetCurrentWorkspace from '@/hooks/useGetCurrentWorkspace';
import useResultTabStore from '@/store/ResultsTabStore';
import { NavProps } from '@/types';
import WorkspaceName from '@/components/Navbar/WorkspaceName';
import NavBarLinks from '@/components/Navbar/NavBarLinks';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import useProjectRequiredFieldsStore, {
  computeRequiredFields,
} from '@/store/ProjectRequiredFieldsStore';

function ProjectNavbar({ isSubscribed, organizationFirstLetter }: NavProps) {
  const location = useLocation();
  const {
    project_id: projectId,
    workspace_id: workspaceId,
    organization_id: organizationId,
  } = useParams();
  const { lang } = useTranslation();
  const { currentTheme, themeName } = useTheme();
  const currentWorkspace = useGetCurrentWorkspace();
  const { name: workspaceName } = currentWorkspace || {};
  const { resetTabs } = useResultTabStore();
  const { currentProject: singleProjectData } = useCurrentProject();
  const setRequiredFields = useProjectRequiredFieldsStore((state) => state.setRequiredFields);

  // Hydrate the publish-required-fields store from the SAVED project on every
  // project route. Without this the store is only ever written by the Design
  // page's live-form effect, so landing/refreshing directly on Share (or any
  // non-Design route) leaves it at its all-empty default — falsely reporting a
  // complete project as "missing required fields" and hard-disabling Publish
  // (TELV1-509). The Design page's live effect still overrides while editing;
  // both use the same pure `computeRequiredFields`, and this only re-runs when
  // `singleProjectData` changes (load / post-save refetch), so it never clobbers
  // SAVED state. An unsaved in-flight edit whose (older) save response lands
  // after the live tick can momentarily re-show as empty; it self-heals on the
  // next live tick, and the impact is advisory-only (missing-fields list +
  // Publish enable).
  useEffect(() => {
    if (singleProjectData) {
      setRequiredFields(
        computeRequiredFields(
          singleProjectData as unknown as Record<string, unknown>,
          singleProjectData?.project_type === 'embed',
        ),
      );
    }
  }, [singleProjectData, setRequiredFields]);

  const isProjectDraft = singleProjectData?.status === EProjectStatus.DRAFT;
  const isDefaultTheme = themeName === ECompanyThemeName.DEFAULT;
  const isEmpathyTheme = themeName === ECompanyThemeName.EMPATHY;
  const projectStatus = singleProjectData?.status;
  const projectTitle = singleProjectData?.title;

  return (
    <nav
      style={{ backgroundColor: currentTheme?.background }}
      className="fixed left-0 right-0 flex h-[55px] items-center z-100 justify-between max-w-full gap-5 px-[2%] py-2 transition-all ease-in border-b"
    >
      {/* Below lg: compact back button replaces the full breadcrumb so
          the Results tab links fit without the organization avatar,
          workspace name, and project title pushing them off-screen.
          The researcher knows which project they're in from the page
          content, the URL, and the browser tab title. */}
      <Link
        to={`${Routes.organization.path}/${organizationId}${Routes.workspace.path}/${workspaceId}`}
        onClick={resetTabs}
        aria-label={lang.get('msg.backToWorkspace')}
        title={lang.get('msg.backToWorkspace')}
        className="flex lg:hidden items-center justify-center h-8 w-8 rounded-md hover:bg-neutral-100 transition-colors"
      >
        <ArrowLeft className="size-5" />
      </Link>

      <div className="hidden lg:flex items-center gap-2 text-[15px] w-fit whitespace-nowrap">
        <Avatar className="object-cover h-full -mr-2.5">
          {/* add img later */}
          <AvatarImage src="" className="object-cover max-w-[25px] max-h-[25px]" />
          <AvatarFallback
            style={{ backgroundColor: currentTheme?.['chat-primary'] }}
            className="h-7 w-7 font-semibold py-0.5 text-white"
          >
            {organizationFirstLetter}
          </AvatarFallback>
        </Avatar>

        {projectTitle && workspaceName && (
          <WorkspaceName
            projectTitle={projectTitle}
            workspaceName={workspaceName}
            routes={Routes}
            organizationId={organizationId}
            workspaceId={workspaceId}
            resetTabs={resetTabs}
          />
        )}
        {/* TELV1-186: project-type pill in the project nav header.
            Surfaces embed identity at the top of every project page
            (Design / Share / Results). Interview projects show no
            pill (default identity, keeps header clean). */}
        <ProjectTypePill projectType={singleProjectData?.project_type} />
      </div>
      <NavBarLinks
        resetTabs={resetTabs}
        organizationId={organizationId}
        workspaceId={workspaceId}
        projectId={projectId}
        lang={lang}
        routes={Routes}
        location={location}
        projectStatus={projectStatus}
        isEmpathyTheme={isEmpathyTheme}
      />

      <div className="flex items-center justify-end gap-4 w-fit">
        {isProjectDraft && !isSubscribed && isDefaultTheme && (
          <a
            className="px-4 py-1.5 text-sm font-semibold text-white rounded-md bg-chat-primary hover:scale-[1.02] transition-all ease-in-out hover:bg-[#ed638a]"
            href="https://tellet.ai/demo"
            target="_blank"
            rel="noreferrer"
          >
            {lang.get('msg.upgradeNow')}
          </a>
        )}

        <ProjectStatusMenu />

        {/* user profile */}
        <button type="button" aria-label="user profile">
          <UserIconDropDown />
        </button>
      </div>
    </nav>
  );
}

export default ProjectNavbar;
